import { Injectable } from '@angular/core';
import { ApplicationContainer, ApplicationSession, Content, PlaybackSession, UserSession } from 'fl-analytics';
import { createLogger, LoggerLevel } from 'fl-foundation';
import { Player } from 'fl-player-interface';
import { ResourceVm } from 'qp-ui-interface';
import { getBuildVersion } from 'qp-utility';
import { environment } from '../../../environments/environment';
import { ConfigService } from '../../shared/services/config/config.service';
import { DeviceHandlerService } from '../../shared/services/device-handler/device-handler.service';
import { FireboltService } from '../../shared/services/firebolt.service';
import { LocalStorageService } from '../../shared/services/local-storage/local-storage.service';
import { getApplicationContainer, getContentFromResource, getDeviceInfo, getPlaybackError, getUserInfo } from './convivaUtils';

@Injectable({
  providedIn: 'root',
})
export class ConvivaService {
  private appSession: ApplicationSession;
  private playbackSession: PlaybackSession;
  private userSession: UserSession | undefined;
  private playbackContent: Content;
  private playbackContainer: ApplicationContainer;
  private playbackSource: string;

  constructor(
    private deviceHandler: DeviceHandlerService,
    private configService: ConfigService,
    private fireboltService: FireboltService,
    private localStorageService: LocalStorageService
  ) {}

  /**
   * Triggered when the app is initialized
   *
   * @returns void
   */
  public async init(): Promise<void> {
    try {
      const deviceID = this.deviceHandler.getCurrentDeviceID();
      const analytics = await import('fl-analytics');
      this.appSession = analytics.getApplicationSession();

      // const optimization params
      const costOptimizationParams = {
        deviceName: (await this.fireboltService.getDeviceName()) ?? 'Development Browser',
        deviceId: this.deviceHandler.getCurrentDeviceID(),
      };

      const convivaConfig = {
        // customerKey: environment.conviva.ckey,
        customerKey: environment.conviva.ckey,
        debug: false, // for Conviva test session only
        // gatewayUrl: environment.conviva.gUrl, // for Conviva test session only
        gatewayUrl: environment.conviva.gUrl, // for Conviva test session only
        application: await this.getApplicationInfo(),
        device: await getDeviceInfo(deviceID, this.fireboltService),
        user: getUserInfo(this.localStorageService),
        logger: createLogger(LoggerLevel.VERBOSE, 'fl-player-conviva'),
        costOptimizationUrl: this.configService.applicationConfig.throttleApiUrl,
        costOptimizationParams,
        costOptimizationRefreshInterval: 600, // 10 minutes
      };

      await this.appSession.initialize([convivaConfig]);
      this.appSession.start({ startupTime: Date.now() });
      this.userSession = this.appSession.createUserSession(getUserInfo(this.localStorageService));
    } catch (error) {
      console.error('ConvivaService.init', error);
    }
  }

  /**
   * Triggered when the app is stopped
   */
  destroy(): void {
    this.appSession?.stop();
  }

  /**
   * Gets the application information
   * */
  async getApplicationInfo(): Promise<{ name: string; version: string; build: string }> {
    return {
      name: 'UNow',
      version: (await this.fireboltService.getDeviceVersion()) || undefined,
      build: getBuildVersion() || undefined,
    };
  }

  /**
   * Triggered when the player is loaded
   *
   * @param resourceVm
   * @param source
   */
  async createConvivaPlaybackSession(resourceVm: ResourceVm, source: string): Promise<void> {
    if (this.playbackSession) this.stopPlayback();
    if (!resourceVm) return;
    const content = getContentFromResource(resourceVm);
    this.playbackContent = content;
    this.playbackSource = source;
    this.playbackContainer = getApplicationContainer(resourceVm);
    this.playbackSession = await this.appSession.createPlaybackSession(content, source, this.playbackContainer);
    this.playbackSession.start();
  }

  /**
   * Attaches the player for event reporting
   *
   * @param playerInstance
   * */
  attachPlayer(playerInstance: Player): void {
    if (this.playbackSession) {
      this.playbackSession.attachPlayer(playerInstance);
    }
  }

  /**
   * Triggered when the player is stopped
   */
  stopPlayback(): void {
    if (this.playbackSession) {
      this.playbackSession.stop();
      this.playbackSession = null;
      this.playbackContainer = null;
      this.playbackContent = null;
      this.playbackSource = null;
    }
  }

  /**
   * Triggered when the player is stopped with an error
   *
   * @param errorCode
   * @param errorDescription
   * @param error
   */
  stopPlaybackWithError(errorCode: string, errorDescription: string, error: unknown): void {
    try {
      if (!this.playbackSession) return;
      this.playbackSession.stop(
        getPlaybackError(errorCode, errorDescription, error, this.playbackContent, this.playbackSource, this.playbackContainer)
      );
      this.playbackSession = null;
      this.playbackContainer = null;
      this.playbackContent = null;
      this.playbackSource = null;
    } catch (_) {
      this.stopPlayback();
    }
  }

  /**
   * Triggered when the user logs in
   */
  loginUser(): void {
    this.userSession?.login(getUserInfo(this.localStorageService));
  }

  /**
   * Triggered when the user logs out
   */
  logoutUser(): void {
    this.userSession?.logout();
  }
}
